import PropTypes from 'prop-types';
import { Component } from 'react';
import InboxConnectSteps from 'InboxConnectUtils/constants/InboxConnectSteps';
import { EXIT_SUCCESS, CHOOSE_PROVIDER_STEP, DETECT_PROVIDER_STEP, START_CONNECT, TEST_MESSAGE_STEP, OPT_INTO_AUTOMATION_STEP } from '../constants/InboxConnectStepKeys';
// @ts-expect-error not typed
import { trackingClientFromService } from '../api/TrackingClient';
import InboxTypes from '../constants/InboxTypes';
import { enumValues } from '../utils/EnumUtils';
import ProviderTypes from '../constants/ProviderTypes';
import { serviceShape } from '../service/ServiceShape';
import { calcNextStep, connectActionToState } from '../state/WizardStateTransforms';
import { jsx as _jsx } from "react/jsx-runtime";
const propTypes = {
  service: serviceShape.isRequired,
  onDone: PropTypes.func.isRequired,
  onStateChange: PropTypes.func,
  inboxCreateContext: PropTypes.object,
  inboxType: PropTypes.oneOf(enumValues(InboxTypes)).isRequired,
  allowAutoDetectFlow: PropTypes.bool,
  allowCustomEmailInput: PropTypes.bool,
  allowedProviders: PropTypes.arrayOf(PropTypes.oneOf(enumValues(ProviderTypes))),
  automationSelected: PropTypes.bool,
  emailAddress: PropTypes.string,
  hideOptInToAutomationStep: PropTypes.bool,
  oauthOptions: PropTypes.object,
  provider: PropTypes.string,
  restrictProvider: PropTypes.bool,
  showExtensionInstall: PropTypes.bool.isRequired,
  showFinishStep: PropTypes.bool.isRequired,
  onCancel: PropTypes.func,
  showInboxAutomationStep: PropTypes.bool,
  whatToExpectDescription: PropTypes.node,
  skipConnectInboxStep: PropTypes.bool
};
export default class InboxConnectWizardCore extends Component {
  constructor(props) {
    super(props);
    this.trackingClient = null;
    this.returnedFromOauth = false;
    const {
      emailAddress,
      provider,
      oauthOptions,
      allowAutoDetectFlow,
      allowCustomEmailInput,
      service
    } = this.props;
    let startStep = allowAutoDetectFlow ? calcNextStep(DETECT_PROVIDER_STEP, Object.assign({}, props, {
      serviceHasAIAutomation: !!service.setEnableAutomation
    })) : calcNextStep(CHOOSE_PROVIDER_STEP, Object.assign({}, props, {
      serviceHasAIAutomation: !!service.setEnableAutomation
    }));
    if (allowCustomEmailInput && !provider) {
      startStep = OPT_INTO_AUTOMATION_STEP;
    }
    this.state = {
      emailAddress: emailAddress || '',
      selectedClient: provider || '',
      shownStep: startStep,
      startStep,
      connectError: null,
      connectedAccount: null
    };
    if (oauthOptions && oauthOptions.connectAction) {
      this.returnedFromOauth = true;
      this.state = Object.assign({}, this.state, connectActionToState(oauthOptions.connectAction));
    }
    this.trackingClient = trackingClientFromService(service);
    this.handleOnWizardDone = this.handleOnWizardDone.bind(this);
    this.handleUpdateEmail = this.handleUpdateEmail.bind(this);
    this.handleUpdateError = this.handleUpdateError.bind(this);
  }
  componentDidMount() {
    // In order to maintain charts that were built on Sidekick provision
    // being the very first step to ever happen, wizard mount will call.
    // To move this forward (and make more sense), `START_CONNECT` will be the subview.
    // Will not do this on return from oauth because it is continued - not start
    if (!this.returnedFromOauth) {
      this.trackingClient.trackStepView('PROVISION_STEP', START_CONNECT);
    }
  }
  componentDidUpdate(prevProps) {
    const currentProvider = this.props.provider;
    const newProvider = prevProps.provider;
    if (newProvider !== currentProvider) {
      this.setState({
        selectedClient: newProvider
      });
    }
    if (this.props.emailAddress && this.state.emailAddress !== this.props.emailAddress) {
      this.setState({
        emailAddress: this.props.emailAddress
      });
    }
  }
  componentWillUnmount() {
    const {
      startStep
    } = this.state;
    const {
      provider
    } = this.props;
    this.setState({
      selectedClient: provider || '',
      shownStep: startStep
    });
  }
  handleOnWizardDone() {
    const {
      connectedAccount
    } = this.state;
    const {
      onDone
    } = this.props;
    this.trackingClient.trackInboxConnected();
    onDone(connectedAccount);
  }
  handleUpdateEmail(emailAddress) {
    this.setState({
      emailAddress
    });
  }
  handleUpdateError(connectError) {
    this.setState({
      connectError
    });
  }
  render() {
    const {
      allowAutoDetectFlow,
      allowCustomEmailInput,
      allowedProviders,
      automationSelected,
      hideOptInToAutomationStep,
      inboxType,
      oauthOptions,
      provider,
      restrictProvider,
      onDone,
      service,
      showExtensionInstall,
      showFinishStep,
      inboxCreateContext,
      onStateChange,
      onCancel,
      showInboxAutomationStep,
      whatToExpectDescription,
      skipConnectInboxStep
    } = this.props;
    const {
      connectError,
      emailAddress,
      selectedClient,
      shownStep,
      connectedAccount,
      selectedAutomation
    } = this.state;
    const ChildComponent = InboxConnectSteps[shownStep].component;
    const setShownStep = requestedStep => {
      const nextStepKey = calcNextStep(requestedStep, {
        allowAutoDetectFlow,
        provider,
        restrictProvider,
        showFinishStep,
        showInboxAutomationStep,
        serviceHasAIAutomation: !!service.setEnableAutomation
      });
      if (nextStepKey === EXIT_SUCCESS) {
        this.trackingClient.trackInboxConnected();
        onDone(connectedAccount);
      } else {
        if (onStateChange) {
          onStateChange({
            cancelable: nextStepKey !== TEST_MESSAGE_STEP
          });
        }
        this.setState({
          shownStep: nextStepKey
        });
      }
    };
    const setSelectedClient = client => this.setState({
      selectedClient: client
    });
    const setSelectedAutomation = selected => this.setState({
      selectedAutomation: selected
    });
    const setConnectedAccount = account => {
      this.setState({
        connectedAccount: account
      });
    };
    return /*#__PURE__*/_jsx(ChildComponent, {
      allowedProviders: allowedProviders,
      connectError: connectError,
      setConnectError: this.handleUpdateError,
      emailAddress: emailAddress || '',
      setEmailAddress: this.handleUpdateEmail,
      isAutodetectFlow: allowAutoDetectFlow,
      isAllowCustomEmailInput: allowCustomEmailInput,
      inboxType: inboxType,
      oauthOptions: oauthOptions,
      portalId: service.getPortalId(),
      restrictProvider: (restrictProvider && !!provider) === true,
      selectedClient: selectedClient,
      service: service,
      setConnectedAccount: setConnectedAccount,
      onWizardDone: this.handleOnWizardDone,
      setSelectedClient: setSelectedClient,
      setShownStep: setShownStep,
      trackingClient: this.trackingClient,
      showExtensionInstall: showExtensionInstall,
      inboxCreateContext: inboxCreateContext,
      onStateChange: onStateChange,
      onCancel: onCancel,
      setSelectedAutomation: setSelectedAutomation,
      selectedAutomation: selectedAutomation,
      showInboxAutomationStep: showInboxAutomationStep,
      whatToExpectDescription: whatToExpectDescription,
      skipConnectInboxStep: skipConnectInboxStep,
      hideOptInToAutomationStep: hideOptInToAutomationStep,
      automationSelected: automationSelected
    });
  }
}
InboxConnectWizardCore.propTypes = propTypes;
InboxConnectWizardCore.defaultProps = {
  allowedProviders: [ProviderTypes.gmail, ProviderTypes.imap, ProviderTypes.outlook365, ProviderTypes.outlook365GccHigh, ProviderTypes.exchange],
  inboxType: InboxTypes.STANDARD,
  showExtensionInstall: true,
  showFinishStep: true,
  showInboxAutomationStep: false,
  allowCustomEmailInput: true
};